import { Action } from "@/api-schema-enums";
import { Can } from "@/casl-provider";
import {
  useOccupationByUuidQuery,
  type OccupationByUuidParameters,
  type OccupationByUuidResponse,
} from "@/common/api/occupation/use-occupation-by-uuid.query";
import { UserTable } from "@/components/data-table/predefined/user-table";
import { DetailHeader } from "@/components/detail-page/detail-header";
import { DetailPageLayout } from "@/components/detail-page/detail-page-layout";
import { DetailPageSkeleton } from "@/components/detail-page/detail-page-skeleton";
import { DetailTabs } from "@/components/detail-page/detail-tabs";
import { SystemIdTab } from "@/components/detail-page/tabs/system-id-tab";
import type { DetailAction, DetailTab } from "@/components/detail-page/types";
import { CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { UuidExistenceWrapper } from "@/components/uuid-existence-wrapper/uuid-existence-wrapper";
import { Fingerprint, GraduationCap, PencilIcon, UsersIcon } from "lucide-react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "wouter";

const OCCUPATION_DETAIL_TRACKING_NAME = "occupation";

export function OccupationDetailView(): ReactElement {
  return (
    <Can I="read" a="Occupation">
      <OccupationDetailQuery />
    </Can>
  );
}

function OccupationDetailQuery(): ReactElement {
  const { t } = useTranslation(["masterdata"]);
  const params = useParams<{ uuid: string }>();
  const uuid = params.uuid ?? "";
  const occupationQuery = useOccupationByUuidQuery(uuid);

  if (occupationQuery.isPending) {
    return <DetailPageSkeleton />;
  }

  return (
    <UuidExistenceWrapper<OccupationByUuidParameters, OccupationByUuidResponse>
      uuid={uuid}
      query={useOccupationByUuidQuery}
      queryResult={occupationQuery}
      notFoundProps={{
        title: t("masterdata:OccupationNotFound"),
        message: t("masterdata:OccupationDoesNotExist"),
        description: t("masterdata:PleaseCheckOccupationIdOrContactSupport"),
        backTo: "~/masterdata/occupation",
        backToLabel: t("masterdata:BackToOccupationOverview"),
      }}
    >
      {occupationQuery.data ? <OccupationDetailContent occupation={occupationQuery.data} /> : null}
    </UuidExistenceWrapper>
  );
}

function OccupationDetailContent({ occupation }: { occupation: OccupationByUuidResponse }): ReactElement {
  const { t } = useTranslation(["masterdata", "common"]);
  const [, navigate] = useLocation();
  const isDeleted = Boolean(occupation.deletedAt);

  const actions: DetailAction[] = isDeleted
    ? []
    : [
        {
          id: "edit",
          label: t("common:Edit"),
          icon: PencilIcon,
          permission: { action: Action.Manage, subject: "Occupation" },
          onClick: () => navigate(`~/masterdata/occupation/${occupation.uuid}/update`),
        },
      ];

  const tabs: DetailTab[] = [
    {
      id: "users",
      label: t("common:Users"),
      icon: UsersIcon,
      content: () => <UserTable name="occupation-users" body={{ occupations: [occupation.uuid] }} />,
    },
    {
      id: "identifiers",
      label: t("masterdata:SystemIds"),
      icon: Fingerprint,
      content: () => (
        <SystemIdTab
          uuid={occupation.uuid}
          createdAt={occupation.createdAt}
          updatedAt={occupation.updatedAt}
          deletedAt={occupation.deletedAt}
          identifiers={[{ id: "v1-id", label: t("masterdata:V1ID"), value: String(occupation.v1Id) }]}
        />
      ),
    },
  ];

  return (
    <DetailPageLayout
      variant={CenteredLayoutVariant.Wide}
      header={
        <DetailHeader
          icon={<GraduationCap />}
          title={occupation.name}
          badges={
            isDeleted ? (
              <Badge variant={BadgeVariant.Outlined} colorVariant={BadgeColorVariant.Error}>
                {t("common:Deleted")}
              </Badge>
            ) : undefined
          }
          actions={actions}
        />
      }
    >
      <DetailTabs sections={tabs} trackingName={OCCUPATION_DETAIL_TRACKING_NAME} />
    </DetailPageLayout>
  );
}
